import React, { createContext, useState, useContext, ReactNode } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

interface Theme {
    background: string;
    color: string;
    toolbarBackground: string;
    borderColor: string;
    toolbarColor: string;
    textColor: string;
    menuContainer: string;
    containerBorder: string;
    playGroundBackground: string;
    blueprintContent: string;
    alpha: string;
    beta: string;
    gamma: string;
    imgColor?: string;
}


export const darkTheme: Theme = {
    background: '#1b1b1c',
    color: 'white',
    toolbarBackground: '#212123',
    borderColor: '#2b2b2d',
    toolbarColor: '#a6a6a7',
    textColor: 'rgba(255, 255, 255, 0.60)',
    menuContainer: '#121212',
    containerBorder: '#404040',
    playGroundBackground: '#141414',
    blueprintContent: '#1b1b1c',
    alpha: '#3b2975',
    beta: '#14381b',
    gamma: '#52122d'
};

export const lightTheme: Theme = {
    background: '#f3f3f3',
    color: 'black',
    toolbarBackground: 'white',
    borderColor: '#ccc',
    toolbarColor: '#333',
    textColor: '#2E2E2E',
    menuContainer: 'white',
    containerBorder: '#eaeaea',
    playGroundBackground: '#f3f3f3',
    blueprintContent: 'white',
    alpha: '#5E36C9',
    beta: '#238E38',
    gamma: '#C43671',
    imgColor: 'invert(100%)'
};


interface ThemeContextProps {
    isDarkTheme: boolean;
    toggleTheme: () => void;
}


interface ThemeProviderProps {
    children: ReactNode;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [isDarkTheme, setIsDarkTheme] = useState<boolean>(() => {
        const savedTheme = localStorage.getItem('isDarkTheme');
        return savedTheme ? JSON.parse(savedTheme) : false;
    });

    const toggleTheme = () => {
        setIsDarkTheme(prevTheme => {
            const newTheme = !prevTheme;
            localStorage.setItem('isDarkTheme', JSON.stringify(newTheme));
            return newTheme;
        });
    };

    const currentTheme = isDarkTheme ? darkTheme : lightTheme;

    return (
        <ThemeContext.Provider value={{ isDarkTheme, toggleTheme }}>
            <StyledThemeProvider theme={currentTheme}>
                {children}
            </StyledThemeProvider>
        </ThemeContext.Provider>
    );
};


export const useTheme = (): ThemeContextProps => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
